import React, { useState, useRef } from 'react';
import { Lock, Calculator, TrendingUp, Users, DollarSign, GitCompare, Info, Share2, Download, FileText, MessageCircle, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { translations } from '../translations';
import CurrentScenario from './CurrentScenario';

const focusSelect = (e) => e.target.select();

const NetworkSimulator = ({ language = 'pt' }) => {
  const t = translations[language].simulator;
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [currentScenarioTotal, setCurrentScenarioTotal] = useState(0);
  const [candidateName, setCandidateName] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const shareCaptureRef = useRef(null);
  
  // Configuração da rede
  const [ownRevenue, setOwnRevenue] = useState(50000);
  
  // Número de pessoas por nível
  const [level1Count, setLevel1Count] = useState(5);
  const [level2Count, setLevel2Count] = useState(3);
  const [level3Count, setLevel3Count] = useState(2);
  const [level4Count, setLevel4Count] = useState(2);
  const [level5Count, setLevel5Count] = useState(1);
  
  // Modo de faturação por nível (uniform ou individual)
  const [level1Mode, setLevel1Mode] = useState('uniform');
  const [level2Mode, setLevel2Mode] = useState('uniform');
  const [level3Mode, setLevel3Mode] = useState('uniform');
  const [level4Mode, setLevel4Mode] = useState('uniform');
  const [level5Mode, setLevel5Mode] = useState('uniform');
  
  // Faturação uniforme por nível
  const [level1Revenue, setLevel1Revenue] = useState(30000);
  const [level2Revenue, setLevel2Revenue] = useState(25000);
  const [level3Revenue, setLevel3Revenue] = useState(20000);
  const [level4Revenue, setLevel4Revenue] = useState(15000);
  const [level5Revenue, setLevel5Revenue] = useState(10000);
  
  // Faturações individuais por pessoa
  const [level1Revenues, setLevel1Revenues] = useState([]);
  const [level2Revenues, setLevel2Revenues] = useState([]);
  const [level3Revenues, setLevel3Revenues] = useState([]);
  const [level4Revenues, setLevel4Revenues] = useState([]);
  const [level5Revenues, setLevel5Revenues] = useState([]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === '1020') {
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert(t.login.errorMessage);
      setPassword('');
    }
  };

  // Função para atualizar receita individual
  const updateIndividualRevenue = (level, index, value, revenues, setRevenues) => {
    const newRevenues = [...revenues];
    newRevenues[index] = Number(value) || 0;
    setRevenues(newRevenues);
  };

  // Função para inicializar receitas individuais quando mudar de modo
  const toggleMode = (level, mode, setMode, count, uniformRevenue, revenues, setRevenues) => {
    const newMode = mode === 'uniform' ? 'individual' : 'uniform';
    setMode(newMode);
    
    // Se mudando para individual, inicializa array com valores uniformes
    if (newMode === 'individual' && revenues.length < count) {
      const newRevenues = Array(count).fill(uniformRevenue);
      setRevenues(newRevenues);
    }
  };

  const calculateEarnings = () => {
    // Comissões próprias (80% da faturação)
    const ownCommission = ownRevenue * 0.8;
    
    // Calcular faturação total por nível
    const getTotalRevenue = (mode, uniformValue, individualValues, count) => {
      if (mode === 'uniform') {
        return count * uniformValue;
      } else {
        return individualValues.slice(0, count).reduce((sum, val) => sum + (val || 0), 0);
      }
    };
    
    // Cada nível é INDEPENDENTE - usuário escolhe quantas pessoas em cada linha
    const totalLevel1 = getTotalRevenue(level1Mode, level1Revenue, level1Revenues, level1Count);
    const totalLevel2 = getTotalRevenue(level2Mode, level2Revenue, level2Revenues, level2Count);
    const totalLevel3 = getTotalRevenue(level3Mode, level3Revenue, level3Revenues, level3Count);
    const totalLevel4 = getTotalRevenue(level4Mode, level4Revenue, level4Revenues, level4Count);
    const totalLevel5 = getTotalRevenue(level5Mode, level5Revenue, level5Revenues, level5Count);
    
    // Comissões por nível
    const commission1 = totalLevel1 * 0.10;
    const commission2 = totalLevel2 * 0.05;
    const commission3 = totalLevel3 * 0.025;
    const commission4 = totalLevel4 * 0.015;
    const commission5 = totalLevel5 * 0.01;
    
    // Total
    const totalCommissions = commission1 + commission2 + commission3 + commission4 + commission5;
    const grandTotal = ownCommission + totalCommissions;
    
    // Total de pessoas na rede - soma direta dos níveis
    const totalPeople1 = level1Count;
    const totalPeople2 = level2Count;
    const totalPeople3 = level3Count;
    const totalPeople4 = level4Count;
    const totalPeople5 = level5Count;
    const totalNetworkSize = totalPeople1 + totalPeople2 + totalPeople3 + totalPeople4 + totalPeople5;
    
    return {
      ownCommission,
      commission1,
      commission2,
      commission3,
      commission4,
      commission5,
      totalCommissions,
      grandTotal,
      totalLevel1,
      totalLevel2,
      totalLevel3,
      totalLevel4,
      totalLevel5,
      totalPeople1,
      totalPeople2,
      totalPeople3,
      totalPeople4,
      totalPeople5,
      totalNetworkSize
    };
  };

  const results = showResults ? calculateEarnings() : null;

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const captureAsCanvas = async () => {
    if (!shareCaptureRef.current) return null;
    return await html2canvas(shareCaptureRef.current, {
      backgroundColor: '#0a0a0a',
      scale: 2,
      useCORS: true,
      logging: false
    });
  };

  const fileBaseName = () => {
    const name = (candidateName || 'simulacao').trim().replace(/\s+/g, '-').toLowerCase();
    const date = new Date().toISOString().slice(0, 10);
    return `cenario-predimed-${name}-${date}`;
  };

  const downloadPNG = async () => {
    setIsGenerating(true);
    try {
      const canvas = await captureAsCanvas();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `${fileBaseName()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadPDF = async () => {
    setIsGenerating(true);
    try {
      const canvas = await captureAsCanvas();
      if (!canvas) return;
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      let imgWidth = pageWidth - 20;
      let imgHeight = imgWidth / ratio;
      if (imgHeight > pageHeight - 20) {
        imgHeight = pageHeight - 20;
        imgWidth = imgHeight * ratio;
      }
      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;
      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`${fileBaseName()}.pdf`);
    } finally {
      setIsGenerating(false);
    }
  };

  const shareWhatsApp = async () => {
    setIsGenerating(true);
    try {
      const canvas = await captureAsCanvas();
      if (!canvas) return;

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png')
      );
      const file = new File([blob], `${fileBaseName()}.png`, { type: 'image/png' });

      const diff = results ? results.grandTotal - 1250 - currentScenarioTotal : 0;
      const msg =
        `Olá${candidateName ? ' ' + candidateName : ''}!\n\n` +
        `Aqui está a simulação de ganhos anuais que preparámos:\n` +
        `• Cenário atual: ${formatCurrency(currentScenarioTotal)}\n` +
        `• Cenário PREDIMED: ${results ? formatCurrency(results.grandTotal - 1250) : '—'}\n` +
        `• Diferença anual: ${diff >= 0 ? '+' : ''}${formatCurrency(diff)}\n\n` +
        `— Miguel Ângelo · Apex Team`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Simulação PREDIMED',
          text: msg
        });
      } else {
        // fallback: download image + open wa.me with text
        const link = document.createElement('a');
        link.download = `${fileBaseName()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(wa, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Componente da Pirâmide Visual
  const PyramidVisualization = () => {
    // Cada nível é independente - usuário escolhe o total de pessoas
    const totalPeople1 = level1Count;
    const totalPeople2 = level2Count;
    const totalPeople3 = level3Count;
    const totalPeople4 = level4Count;
    const totalPeople5 = level5Count;

    const maxWidth = 400;
    const levelHeight = 60;

    return (
      <div className="flex flex-col items-center justify-center space-y-1 p-8">
        {/* Você (topo da pirâmide) */}
        <div className="flex flex-col items-center mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-gold to-gold-light rounded-lg flex items-center justify-center shadow-lg border-2 border-gold">
            <Users className="h-8 w-8 text-black" />
          </div>
          <p className="text-xs text-gold font-semibold mt-2">{t.pyramid.you}</p>
          <p className="text-xs text-gray-400">{t.pyramid.youCommission}</p>
        </div>

        {/* Linha conectora */}
        <div className="w-0.5 h-6 bg-gradient-to-b from-gold to-gold/50"></div>

        {/* Nível 1 */}
        <div className="flex flex-col items-center w-full" style={{ maxWidth: `${maxWidth}px` }}>
          <div 
            className="flex items-center justify-center gap-1 flex-wrap p-3 bg-gold/10 border-2 border-gold rounded-lg backdrop-blur-sm"
            style={{ minHeight: `${levelHeight}px` }}
          >
            {totalPeople1 > 0 ? (
              <>
                {[...Array(Math.min(totalPeople1, 20))].map((_, i) => (
                  <div key={i} className="w-8 h-8 bg-gold/80 rounded-full flex items-center justify-center">
                    <span className="text-xs text-black font-bold">1</span>
                  </div>
                ))}
                {totalPeople1 > 20 && (
                  <div className="text-gold font-bold">+{totalPeople1 - 20}</div>
                )}
              </>
            ) : (
              <div className="text-gray-500 text-sm">{t.config.level} 1 {t.config.emptyLevel}</div>
            )}
          </div>
          <p className="text-xs text-gold font-semibold mt-1">
            {t.config.level} 1: {totalPeople1} {t.results.peopleCount} (10%)
          </p>
        </div>

        {totalPeople1 > 0 && <div className="w-0.5 h-4 bg-gold/40"></div>}

        {/* Nível 2 */}
        {totalPeople2 > 0 && (
          <>
            <div className="flex flex-col items-center w-full" style={{ maxWidth: `${maxWidth}px` }}>
              <div 
                className="flex items-center justify-center gap-1 flex-wrap p-3 bg-gold/10 border-2 border-gold/80 rounded-lg backdrop-blur-sm"
                style={{ minHeight: `${levelHeight}px` }}
              >
                {[...Array(Math.min(totalPeople2, 25))].map((_, i) => (
                  <div key={i} className="w-7 h-7 bg-gold/70 rounded-full flex items-center justify-center">
                    <span className="text-xs text-black font-bold">2</span>
                  </div>
                ))}
                {totalPeople2 > 25 && (
                  <div className="text-gold font-bold text-sm">+{totalPeople2 - 25}</div>
                )}
              </div>
              <p className="text-xs text-gold/80 font-semibold mt-1">
                {t.config.level} 2: {totalPeople2} {t.results.peopleCount} (5%)
              </p>
            </div>
            <div className="w-0.5 h-4 bg-gold/30"></div>
          </>
        )}

        {/* Nível 3 */}
        {totalPeople3 > 0 && (
          <>
            <div className="flex flex-col items-center w-full" style={{ maxWidth: `${maxWidth}px` }}>
              <div 
                className="flex items-center justify-center gap-1 flex-wrap p-2 bg-gold/10 border-2 border-gold/60 rounded-lg backdrop-blur-sm"
                style={{ minHeight: `${levelHeight}px` }}
              >
                {[...Array(Math.min(totalPeople3, 30))].map((_, i) => (
                  <div key={i} className="w-6 h-6 bg-gold/60 rounded-full flex items-center justify-center">
                    <span className="text-xs text-black font-bold">3</span>
                  </div>
                ))}
                {totalPeople3 > 30 && (
                  <div className="text-gold/80 font-bold text-sm">+{totalPeople3 - 30}</div>
                )}
              </div>
              <p className="text-xs text-gold/60 font-semibold mt-1">
                {t.config.level} 3: {totalPeople3} {t.results.peopleCount} (2.5%)
              </p>
            </div>
            <div className="w-0.5 h-4 bg-gold/20"></div>
          </>
        )}

        {/* Nível 4 */}
        {totalPeople4 > 0 && (
          <>
            <div className="flex flex-col items-center w-full" style={{ maxWidth: `${maxWidth}px` }}>
              <div 
                className="flex items-center justify-center gap-1 flex-wrap p-2 bg-gold/10 border-2 border-gold/40 rounded-lg backdrop-blur-sm"
                style={{ minHeight: `${levelHeight}px` }}
              >
                {[...Array(Math.min(totalPeople4, 35))].map((_, i) => (
                  <div key={i} className="w-5 h-5 bg-gold/40 rounded-full flex items-center justify-center">
                    <span className="text-xs text-black font-bold">4</span>
                  </div>
                ))}
                {totalPeople4 > 35 && (
                  <div className="text-gold/60 font-bold text-xs">+{totalPeople4 - 35}</div>
                )}
              </div>
              <p className="text-xs text-gold/40 font-semibold mt-1">
                {t.config.level} 4: {totalPeople4} {t.results.peopleCount} (1.5%)
              </p>
            </div>
            <div className="w-0.5 h-4 bg-gold/10"></div>
          </>
        )}

        {/* Nível 5 */}
        {totalPeople5 > 0 && (
          <div className="flex flex-col items-center w-full" style={{ maxWidth: `${maxWidth}px` }}>
            <div 
              className="flex items-center justify-center gap-1 flex-wrap p-2 bg-gold/10 border-2 border-gold/20 rounded-lg backdrop-blur-sm"
              style={{ minHeight: `${levelHeight}px` }}
            >
              {[...Array(Math.min(totalPeople5, 40))].map((_, i) => (
                <div key={i} className="w-4 h-4 bg-gold/20 rounded-full flex items-center justify-center">
                  <span className="text-xs text-black font-bold">5</span>
                </div>
              ))}
              {totalPeople5 > 40 && (
                <div className="text-gold/40 font-bold text-xs">+{totalPeople5 - 40}</div>
              )}
            </div>
            <p className="text-xs text-gold/20 font-semibold mt-1">
              {t.config.level} 5: {totalPeople5} {t.results.peopleCount} (1%)
            </p>
          </div>
        )}

        {/* Total da Rede */}
        <div className="mt-6 text-center">
          <p className="text-2xl font-bold text-gold">
            {totalPeople1 + totalPeople2 + totalPeople3 + totalPeople4 + totalPeople5}
          </p>
          <p className="text-xs text-gray-400">{t.pyramid.totalPeople}</p>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <section className="py-20 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-md mx-auto">
            <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-gradient-to-r from-gold to-gold-light rounded-full flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-black" />
                </div>
                <CardTitle className="text-2xl text-white heading-luxury">
                  {t.login.title}
                </CardTitle>
                <p className="text-gray-400 mt-2">{t.login.restrictedAccess}</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="password" className="text-white">{t.login.password}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      placeholder={t.login.passwordPlaceholder}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold"
                  >
                    {t.login.submit}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-black min-h-screen">
      <div className="container mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-5xl md:text-6xl font-bold text-white heading-luxury mb-4">
            {t.title}
          </h2>
          <div className="section-divider" />
          <p className="text-gray-400 text-lg">
            {t.subtitle}
          </p>
        </div>

        {/* Candidate name — used in the shareable export */}
        <div className="max-w-xl mx-auto mb-10">
          <Label className="text-gray-400 text-xs uppercase tracking-wider">
            Nome (aparece na partilha)
          </Label>
          <Input
            type="text"
            value={candidateName}
            onFocus={focusSelect}
            onChange={(e) => setCandidateName(e.target.value)}
            placeholder="Ex: João Silva"
            className="mt-2 bg-white/10 border-gold text-white"
            data-testid="simulator-candidate-name"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Cenário Atual (NOVO) */}
          <div className="order-1 xl:order-1">
            <CurrentScenario onTotalChange={setCurrentScenarioTotal} />
          </div>

          {/* Cenário PREDIMED — Configuração */}
          <div className="space-y-6 order-3 xl:order-2">
            <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-gold" />
                  Cenário PREDIMED
                </CardTitle>
                <p className="text-gray-400 text-xs">
                  Comissões de 80% sobre a sua faturação.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-white">{t.config.ownRevenueLabel}</Label>
                    <Input
                      type="number"
                      value={ownRevenue}
                      onFocus={focusSelect}
                      onChange={(e) => setOwnRevenue(Number(e.target.value))}
                      onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      data-testid="predimed-own-revenue"
                    />
                    <p className="text-xs text-gray-400 mt-1">{t.config.ownRevenueHelper}</p>
                  </div>

                  <div className="pt-3 border-t border-gold/20">
                    <div className="flex items-start gap-2 p-3 rounded-md bg-gold/5 border border-gold/20">
                      <Info className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-gray-300 leading-relaxed">
                        O sistema PREDIMED tem um <span className="text-gold font-semibold">fee anual de 1.250€ + IVA</span>,
                        a descontar nas comissões de venda e de rede.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-gold" />
                  {t.config.networkConfig}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Nível 1 */}
                  <div className="border-l-4 border-gold pl-4 space-y-3">
                    <Label className="text-gold font-semibold">{t.config.level} 1 (10% {t.config.commission})</Label>
                    
                    <div>
                      <Label className="text-white text-xs">{t.config.people}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={level1Count}
                        onChange={(e) => setLevel1Count(Number(e.target.value))}
                        onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="level1Uniform"
                        checked={level1Mode === 'uniform'}
                        onChange={() => toggleMode(1, level1Mode, setLevel1Mode, level1Count, level1Revenue, level1Revenues, setLevel1Revenues)}
                        className="w-4 h-4 rounded border-gold"
                      />
                      <Label htmlFor="level1Uniform" className="text-white text-xs cursor-pointer">
                        {t.config.uniformRevenue}
                      </Label>
                    </div>

                    {level1Mode === 'uniform' ? (
                      <div>
                        <Label className="text-white text-xs">{t.config.revenue}</Label>
                        <Input
                          type="number"
                          value={level1Revenue}
                          onChange={(e) => setLevel1Revenue(Number(e.target.value))}
                          onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        <Label className="text-white text-xs">{t.config.individualRevenue}</Label>
                        {Array.from({ length: level1Count }, (_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-gold text-xs w-16">{t.config.person} {i + 1}:</span>
                            <Input
                              type="number"
                              value={level1Revenues[i] || 0}
                              onChange={(e) => updateIndividualRevenue(1, i, e.target.value, level1Revenues, setLevel1Revenues)}
                              className="bg-white/10 border-gold text-white text-sm h-8"
                              placeholder="€"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nível 2 */}
                  <div className="border-l-4 border-gold/80 pl-4 space-y-3">
                    <Label className="text-gold/80 font-semibold">{t.config.level} 2 (5% {t.config.commission})</Label>
                    
                    <div>
                      <Label className="text-white text-xs">{t.config.people}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={level2Count}
                        onChange={(e) => setLevel2Count(Number(e.target.value))}
                        onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="level2Uniform"
                        checked={level2Mode === 'uniform'}
                        onChange={() => toggleMode(2, level2Mode, setLevel2Mode, level2Count, level2Revenue, level2Revenues, setLevel2Revenues)}
                        className="w-4 h-4 rounded border-gold"
                      />
                      <Label htmlFor="level2Uniform" className="text-white text-xs cursor-pointer">
                        {t.config.uniformRevenue}
                      </Label>
                    </div>

                    {level2Mode === 'uniform' ? (
                      <div>
                        <Label className="text-white text-xs">{t.config.revenue}</Label>
                        <Input
                          type="number"
                          value={level2Revenue}
                          onChange={(e) => setLevel2Revenue(Number(e.target.value))}
                          onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        <Label className="text-white text-xs">{t.config.individualRevenue}</Label>
                        {Array.from({ length: level2Count }, (_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-gold/80 text-xs w-16">{t.config.person} {i + 1}:</span>
                            <Input
                              type="number"
                              value={level2Revenues[i] || 0}
                              onChange={(e) => updateIndividualRevenue(2, i, e.target.value, level2Revenues, setLevel2Revenues)}
                              className="bg-white/10 border-gold text-white text-sm h-8"
                              placeholder="€"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nível 3 */}
                  <div className="border-l-4 border-gold/60 pl-4 space-y-3">
                    <Label className="text-gold/60 font-semibold">{t.config.level} 3 (2.5% {t.config.commission})</Label>
                    
                    <div>
                      <Label className="text-white text-xs">{t.config.people}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={level3Count}
                        onChange={(e) => setLevel3Count(Number(e.target.value))}
                        onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="level3Uniform"
                        checked={level3Mode === 'uniform'}
                        onChange={() => toggleMode(3, level3Mode, setLevel3Mode, level3Count, level3Revenue, level3Revenues, setLevel3Revenues)}
                        className="w-4 h-4 rounded border-gold"
                      />
                      <Label htmlFor="level3Uniform" className="text-white text-xs cursor-pointer">
                        {t.config.uniformRevenue}
                      </Label>
                    </div>

                    {level3Mode === 'uniform' ? (
                      <div>
                        <Label className="text-white text-xs">{t.config.revenue}</Label>
                        <Input
                          type="number"
                          value={level3Revenue}
                          onChange={(e) => setLevel3Revenue(Number(e.target.value))}
                          onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        <Label className="text-white text-xs">{t.config.individualRevenue}</Label>
                        {Array.from({ length: level3Count }, (_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-gold/60 text-xs w-16">{t.config.person} {i + 1}:</span>
                            <Input
                              type="number"
                              value={level3Revenues[i] || 0}
                              onChange={(e) => updateIndividualRevenue(3, i, e.target.value, level3Revenues, setLevel3Revenues)}
                              className="bg-white/10 border-gold text-white text-sm h-8"
                              placeholder="€"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nível 4 */}
                  <div className="border-l-4 border-gold/40 pl-4 space-y-3">
                    <Label className="text-gold/40 font-semibold">{t.config.level} 4 (1.5% {t.config.commission})</Label>
                    
                    <div>
                      <Label className="text-white text-xs">{t.config.people}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={level4Count}
                        onChange={(e) => setLevel4Count(Number(e.target.value))}
                        onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="level4Uniform"
                        checked={level4Mode === 'uniform'}
                        onChange={() => toggleMode(4, level4Mode, setLevel4Mode, level4Count, level4Revenue, level4Revenues, setLevel4Revenues)}
                        className="w-4 h-4 rounded border-gold"
                      />
                      <Label htmlFor="level4Uniform" className="text-white text-xs cursor-pointer">
                        {t.config.uniformRevenue}
                      </Label>
                    </div>

                    {level4Mode === 'uniform' ? (
                      <div>
                        <Label className="text-white text-xs">{t.config.revenue}</Label>
                        <Input
                          type="number"
                          value={level4Revenue}
                          onChange={(e) => setLevel4Revenue(Number(e.target.value))}
                          onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        <Label className="text-white text-xs">{t.config.individualRevenue}</Label>
                        {Array.from({ length: level4Count }, (_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-gold/40 text-xs w-16">{t.config.person} {i + 1}:</span>
                            <Input
                              type="number"
                              value={level4Revenues[i] || 0}
                              onChange={(e) => updateIndividualRevenue(4, i, e.target.value, level4Revenues, setLevel4Revenues)}
                              className="bg-white/10 border-gold text-white text-sm h-8"
                              placeholder="€"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Nível 5 */}
                  <div className="border-l-4 border-gold/20 pl-4 space-y-3">
                    <Label className="text-gold/20 font-semibold">{t.config.level} 5 (1% {t.config.commission})</Label>
                    
                    <div>
                      <Label className="text-white text-xs">{t.config.people}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={level5Count}
                        onChange={(e) => setLevel5Count(Number(e.target.value))}
                        onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="level5Uniform"
                        checked={level5Mode === 'uniform'}
                        onChange={() => toggleMode(5, level5Mode, setLevel5Mode, level5Count, level5Revenue, level5Revenues, setLevel5Revenues)}
                        className="w-4 h-4 rounded border-gold"
                      />
                      <Label htmlFor="level5Uniform" className="text-white text-xs cursor-pointer">
                        {t.config.uniformRevenue}
                      </Label>
                    </div>

                    {level5Mode === 'uniform' ? (
                      <div>
                        <Label className="text-white text-xs">{t.config.revenue}</Label>
                        <Input
                          type="number"
                          value={level5Revenue}
                          onChange={(e) => setLevel5Revenue(Number(e.target.value))}
                          onFocus={focusSelect}
                        className="bg-white/10 border-gold text-white"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                        <Label className="text-white text-xs">{t.config.individualRevenue}</Label>
                        {Array.from({ length: level5Count }, (_, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-gold/20 text-xs w-16">{t.config.person} {i + 1}:</span>
                            <Input
                              type="number"
                              value={level5Revenues[i] || 0}
                              onChange={(e) => updateIndividualRevenue(5, i, e.target.value, level5Revenues, setLevel5Revenues)}
                              className="bg-white/10 border-gold text-white text-sm h-8"
                              placeholder="€"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={() => setShowResults(true)}
              className="w-full bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold py-6 text-lg"
            >
              <Calculator className="h-5 w-5 mr-2" />
              {t.calculate}
            </Button>
          </div>

          {/* Pirâmide Visual - sempre visível */}
          <div className="order-2 xl:order-3">
            <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm xl:sticky xl:top-6">
              <CardHeader>
                <CardTitle className="text-white text-center">{t.pyramid.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <PyramidVisualization />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Resultados (full-width abaixo) */}
        {showResults && results && (
          <div className="max-w-7xl mx-auto mt-10 space-y-6">
            {/* Capture area — used for PNG/PDF export */}
            <div
              ref={shareCaptureRef}
              className="bg-black p-8 rounded-2xl"
              data-testid="share-capture-area"
            >
              {/* Branded header (visible in export) */}
              <div className="flex items-center justify-between mb-6 pb-5 border-b border-gold/30">
                <div>
                  <div className="text-gold text-xs uppercase tracking-[0.3em] mb-1">
                    Simulação PREDIMED
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white heading-luxury">
                    {candidateName || 'Candidato'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white font-bold heading-luxury text-lg">
                    Miguel Ângelo
                  </div>
                  <div className="text-gold text-xs tracking-wider">
                    APEX TEAM
                  </div>
                  <div className="text-gray-500 text-[10px] mt-1">
                    {new Date().toLocaleDateString('pt-PT')}
                  </div>
                </div>
              </div>

              {/* Comparação Atual vs PREDIMED */}
              <Card className="border-2 border-gold bg-gradient-to-br from-gold/10 to-black/80 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-2xl">
                    <GitCompare className="h-6 w-6 text-gold" />
                    Comparação — Atual vs PREDIMED
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
                    <div className="p-6 rounded-xl bg-black/60 border border-gray-600 text-center">
                      <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                        Cenário Atual
                      </div>
                      <div
                        className="text-3xl md:text-4xl font-bold text-white heading-luxury"
                        data-testid="comparison-current-total"
                      >
                        {formatCurrency(currentScenarioTotal)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">por ano</div>
                    </div>

                    <div className="text-center">
                      <div
                        className={`text-5xl font-bold heading-luxury ${
                          results.grandTotal - 1250 - currentScenarioTotal >= 0
                            ? 'text-green-400'
                            : 'text-red-400'
                        }`}
                        data-testid="comparison-diff"
                      >
                        {results.grandTotal - 1250 - currentScenarioTotal >= 0 ? '+' : ''}
                        {formatCurrency(results.grandTotal - 1250 - currentScenarioTotal)}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-gray-400 mt-2">
                        Diferença anual
                      </div>
                    </div>

                    <div className="p-6 rounded-xl bg-gradient-to-br from-gold/20 to-gold-light/5 border border-gold text-center">
                      <div className="text-xs uppercase tracking-wider text-gold mb-2">
                        Cenário PREDIMED
                      </div>
                      <div
                        className="text-3xl md:text-4xl font-bold text-gold heading-luxury"
                        data-testid="comparison-predimed-total"
                      >
                        {formatCurrency(results.grandTotal - 1250)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        por ano (já descontado fee de 1.250€)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Footer in export */}
              <div className="mt-6 pt-4 border-t border-gold/20 text-center text-xs text-gray-500">
                Simulação indicativa · www.miguelangelo.pt · Miguel Ângelo — Apex Team · PREDIMED PRIME
              </div>
            </div>

            {/* Share button (NOT captured in export) */}
            <div className="flex justify-center">
              <Button
                onClick={() => setShareOpen(true)}
                size="lg"
                className="bg-gradient-to-r from-gold to-gold-light hover:from-gold-light hover:to-gold text-black font-semibold px-8 py-6 text-lg"
                data-testid="share-scenario-btn"
              >
                <Share2 className="mr-2 h-5 w-5" />
                Partilhar Cenário
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border-2 border-gold bg-gradient-to-br from-gold/20 to-gold-light/10 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2 text-2xl">
                    <TrendingUp className="h-6 w-6 text-gold" />
                    {t.results.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-5xl font-bold text-gold heading-luxury">
                      {formatCurrency(results.grandTotal)}
                    </p>
                    <p className="text-gray-400 mt-2">{t.results.perYear}</p>
                    <p className="text-2xl text-white mt-4">
                      {formatCurrency(results.grandTotal / 12)}{t.results.perMonth}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white">{t.results.breakdown}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center pb-3 border-b border-gold/30">
                      <span className="text-white font-semibold">{t.results.ownCommission}</span>
                      <span className="text-gold font-bold">{formatCurrency(results.ownCommission)}</span>
                    </div>
                    
                    <div className="space-y-2">
                      <p className="text-gray-400 text-sm font-semibold">{t.results.networkCommissions}</p>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm">
                          {t.config.level} 1 ({results.totalPeople1} {t.results.peopleCount} × 10%)
                        </span>
                        <span className="text-gold">{formatCurrency(results.commission1)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm">
                          {t.config.level} 2 ({results.totalPeople2} {t.results.peopleCount} × 5%)
                        </span>
                        <span className="text-gold">{formatCurrency(results.commission2)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm">
                          {t.config.level} 3 ({results.totalPeople3} {t.results.peopleCount} × 2.5%)
                        </span>
                        <span className="text-gold">{formatCurrency(results.commission3)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm">
                          {t.config.level} 4 ({results.totalPeople4} {t.results.peopleCount} × 1.5%)
                        </span>
                        <span className="text-gold">{formatCurrency(results.commission4)}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm">
                          {t.config.level} 5 ({results.totalPeople5} {t.results.peopleCount} × 1%)
                        </span>
                        <span className="text-gold">{formatCurrency(results.commission5)}</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-3 border-t border-gold/30">
                      <span className="text-white font-semibold">{t.results.networkTotal}</span>
                      <span className="text-gold font-bold">{formatCurrency(results.totalCommissions)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-gold bg-black/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white">{t.results.networkSize}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-gold">{results.totalNetworkSize}</p>
                    <p className="text-gray-400">{t.pyramid.peopleInNetwork}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-zinc-950 border-gold/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white heading-luxury text-2xl flex items-center gap-2">
              <Share2 className="h-5 w-5 text-gold" />
              Partilhar Cenário
            </DialogTitle>
          </DialogHeader>

          <p className="text-gray-400 text-sm">
            Escolha como enviar a simulação
            {candidateName ? ` a ${candidateName}` : ''}.
          </p>

          <div className="grid grid-cols-1 gap-3 mt-2">
            <Button
              onClick={downloadPNG}
              disabled={isGenerating}
              className="justify-start bg-white/10 hover:bg-white/20 text-white border border-gold/30 py-6"
              data-testid="share-download-png"
            >
              <Download className="mr-3 h-5 w-5 text-gold" />
              <div className="text-left">
                <div className="font-semibold">Baixar Imagem (PNG)</div>
                <div className="text-xs text-gray-400">
                  Ideal para enviar em qualquer app
                </div>
              </div>
            </Button>

            <Button
              onClick={downloadPDF}
              disabled={isGenerating}
              className="justify-start bg-white/10 hover:bg-white/20 text-white border border-gold/30 py-6"
              data-testid="share-download-pdf"
            >
              <FileText className="mr-3 h-5 w-5 text-gold" />
              <div className="text-left">
                <div className="font-semibold">Baixar PDF</div>
                <div className="text-xs text-gray-400">
                  Documento formatado A4
                </div>
              </div>
            </Button>

            <Button
              onClick={shareWhatsApp}
              disabled={isGenerating}
              className="justify-start bg-[#25D366]/20 hover:bg-[#25D366]/30 text-white border border-[#25D366]/60 py-6"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="mr-3 h-5 w-5 text-[#25D366]" />
              <div className="text-left">
                <div className="font-semibold">Partilhar no WhatsApp</div>
                <div className="text-xs text-gray-400">
                  Abre o WhatsApp com a imagem e mensagem
                </div>
              </div>
            </Button>
          </div>

          {isGenerating && (
            <p className="text-gold text-xs text-center animate-pulse">
              A gerar imagem…
            </p>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default NetworkSimulator;
